import { clamp01, smoothstep } from '@/lib/dots/paint'
import { createNoise3D, createRandom } from '@/lib/dots/noise'

// A dot field maps a cell to an intensity between 0 and 1.
// u and v are centered coordinates where 1 unit equals the canvas' shorter side, t is time in seconds.
// col and row are the integer cell indices, for patterns that must snap to the grid.

export type DotGrid = {
    cols: number
    rows: number
    cellPx: number
    width: number
    height: number
}

export type DotField = ((u: number, v: number, t: number, col: number, row: number) => number) & {
    prepare?: (grid: DotGrid) => void
}

type RibbonOptions = {
    angle: number
    length: number
    width: number
    offset: number
    waveAmp: [number, number]
    waveFreq: [number, number]
    waveSpeed: [number, number]
    twistFreq: number
    twistSpeed: number
    phase: number
    strength: number
}

type Ribbon = RibbonOptions & {
    cos: number
    sin: number
    reach: number
}

const createRibbon = (options: RibbonOptions): Ribbon => ({
    ...options,
    cos: Math.cos(options.angle),
    sin: Math.sin(options.angle),
    reach: Math.abs(options.waveAmp[0]) + Math.abs(options.waveAmp[1]) + options.width + 0.12
})

// Two twisting silk ribbons flowing diagonally, the hero visual.
// tilt rotates both ribbons (in radians), so drifting copies elsewhere on the page can lean differently.
export const createSilkField = (seed = 3, tilt = 0): DotField => {
    const noise = createNoise3D(seed)

    const ribbons: Array<Ribbon> = [
        createRibbon({
            angle: -0.62 + tilt,
            length: 0.86,
            width: 0.2,
            offset: 0,
            waveAmp: [0.11, 0.04],
            waveFreq: [2.6, 5.9],
            waveSpeed: [0.36, -0.58],
            twistFreq: 2.1,
            twistSpeed: 0.28,
            phase: 0,
            strength: 1
        }),
        createRibbon({
            angle: -0.48 + tilt,
            length: 0.66,
            width: 0.11,
            offset: 0.1,
            waveAmp: [0.12, 0.03],
            waveFreq: [1.9, 6.8],
            waveSpeed: [-0.28, 0.46],
            twistFreq: 2.9,
            twistSpeed: -0.4,
            phase: 2.1,
            strength: 0.78
        })
    ]

    const field: DotField = (u, v, t) => {
        let near = false

        for (let k = 0; k < ribbons.length; k++) {
            const ribbon = ribbons[k]
            const along = u * ribbon.cos + v * ribbon.sin
            const across = -u * ribbon.sin + v * ribbon.cos - ribbon.offset

            if (Math.abs(along) < ribbon.length + 0.12 && Math.abs(across) < ribbon.reach) {
                near = true
                break
            }
        }

        if (!near) return 0

        const broad = noise(u * 1.25, v * 1.25, t * 0.1)
        const detail = noise(u * 3.2 + 11.7, v * 3.2 - 4.3, t * 0.2)

        let value = 0

        for (let k = 0; k < ribbons.length; k++) {
            const ribbon = ribbons[k]
            const a = u * ribbon.cos + v * ribbon.sin
            const b = -u * ribbon.sin + v * ribbon.cos
            const along = Math.abs(a + broad * 0.1) / ribbon.length

            if (along >= 1) continue

            const center = ribbon.offset
                + ribbon.waveAmp[0] * Math.sin(a * ribbon.waveFreq[0] + t * ribbon.waveSpeed[0] + ribbon.phase)
                + ribbon.waveAmp[1] * Math.sin(a * ribbon.waveFreq[1] + t * ribbon.waveSpeed[1] + ribbon.phase * 1.7)
                + broad * 0.06

            const twist = a * ribbon.twistFreq - t * ribbon.twistSpeed + ribbon.phase + broad * 0.7
            const facing = Math.abs(Math.cos(twist))
            const half = ribbon.width * (1 - along * along) * (0.16 + 0.84 * facing)
            const across = (b - center + detail * 0.028) / half
            const distance = Math.abs(across)

            if (distance >= 1) continue

            const body = 1 - smoothstep(0.55, 1, distance)
            const shade = clamp01(0.5 + 0.38 * across * Math.sin(twist) + 0.34 * (1 - facing))
            const fade = 1 - smoothstep(0.6, 1, along)
            const ribbonValue = body * shade * fade * ribbon.strength

            if (ribbonValue > value) value = ribbonValue
        }

        return value
    }

    return field
}

// A dotted planet with drifting continents and a thin orbit, for cloud work.
export const createGlobeField = (seed = 11): DotField => {
    const noise = createNoise3D(seed)
    const radius = 0.38
    const tilt = 0.4
    const tiltCos = Math.cos(tilt)
    const tiltSin = Math.sin(tilt)
    const lightX = -0.52
    const lightY = -0.56
    const lightZ = 0.64

    const field: DotField = (u, v, t) => {
        const distance = Math.sqrt(u * u + v * v)

        if (distance >= radius) {
            const ex = u / (radius * 1.6)
            const ey = (v + u * 0.18) / (radius * 0.42)
            const ellipse = Math.sqrt(ex * ex + ey * ey)
            const ring = 1 - Math.abs(ellipse - 1) / 0.16

            const orbit = t * 0.6
            const sx = Math.cos(orbit) * radius * 1.6
            const sy = Math.sin(orbit) * radius * 0.42 - sx * 0.18
            const dx = u - sx
            const dy = v - sy
            const satellite = Math.exp(-(dx * dx + dy * dy) / 0.0014)

            return Math.max(ring > 0 ? ring * 0.42 : 0, satellite)
        }

        const nx = u / radius
        const ny = v / radius
        const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny))
        const ty = ny * tiltCos - nz * tiltSin
        const tz = ny * tiltSin + nz * tiltCos
        const spin = t * 0.32
        const spinCos = Math.cos(spin)
        const spinSin = Math.sin(spin)
        const bx = nx * spinCos + tz * spinSin
        const bz = tz * spinCos - nx * spinSin
        const land = noise(bx * 1.7, ty * 1.7, bz * 1.7)
        const light = Math.max(0, nx * lightX + ny * lightY + nz * lightZ)
        const rim = smoothstep(0.82, 1, distance / radius) * 0.3
        const surface = land > 0.08 ? 0.42 + 0.58 * light : 0.08 + 0.22 * light

        return Math.max(surface, rim)
    }

    return field
}

// Two moving wave sources interfering, for data and AI work.
export const createRippleField = (): DotField => {
    const field: DotField = (u, v, t) => {
        const x1 = -0.34 + 0.06 * Math.sin(t * 0.37)
        const y1 = 0.05 * Math.cos(t * 0.29)
        const x2 = 0.34 + 0.05 * Math.cos(t * 0.31)
        const y2 = -0.06 * Math.sin(t * 0.43)
        const d1 = Math.sqrt((u - x1) * (u - x1) + (v - y1) * (v - y1))
        const d2 = Math.sqrt((u - x2) * (u - x2) + (v - y2) * (v - y2))
        const wave = Math.cos(d1 * 32 - t * 2.4) + Math.cos(d2 * 32 - t * 2.4)
        const falloff = Math.exp(-(u * u * 0.7 + v * v * 2.4) * 1.8)

        return clamp01((0.5 + 0.25 * wave) * falloff * 1.2)
    }

    return field
}

// Lanes of streaming packets that snap to the grid rows, for engineering work.
export const createStreamField = (seed = 5): DotField => {
    const random = createRandom(seed)
    const lanes = 64
    const speed = new Float32Array(lanes)
    const offset = new Float32Array(lanes)
    const frequency = new Float32Array(lanes)
    const duty = new Float32Array(lanes)
    const strength = new Float32Array(lanes)

    for (let i = 0; i < lanes; i++) {
        speed[i] = 0.12 + random() * 0.42
        offset[i] = random() * 10
        frequency[i] = 0.9 + random() * 1.8
        duty[i] = 0.18 + random() * 0.42
        strength[i] = 0.55 + random() * 0.45
    }

    let cols = 1

    const field: DotField = (u, v, t, col, row) => {
        if (row % 2 === 1) return 0

        const lane = (row >> 1) % lanes
        const edge = Math.min(col, cols - 1 - col)
        const fade = edge >= 6 ? 1 : Math.max(0, edge) / 6
        const x = u * frequency[lane] - t * speed[lane] + offset[lane]
        const phase = x - Math.floor(x)

        if (phase > duty[lane]) return 0.03 * fade

        const head = phase / duty[lane]

        return (0.28 + 0.72 * head) * strength[lane] * fade
    }

    field.prepare = grid => {
        cols = grid.cols
    }

    return field
}

// A shape morphing between circle and square with vector anchor points, for design work.
export const createMorphField = (): DotField => {
    const size = 0.33
    const anchor = 0.045

    const field: DotField = (u, v, t) => {
        const angle = t * 0.22
        const angleCos = Math.cos(angle)
        const angleSin = Math.sin(angle)
        const x = u * angleCos + v * angleSin
        const y = -u * angleSin + v * angleCos
        const ax = Math.abs(x)
        const ay = Math.abs(y)

        if ((Math.abs(ax - size) < anchor && ay < anchor) || (Math.abs(ay - size) < anchor && ax < anchor)) return 1

        const power = 2 + (0.5 + 0.5 * Math.sin(t * 0.7)) * 4
        const radius = Math.pow(Math.pow(ax, power) + Math.pow(ay, power), 1 / power)
        const outline = 1 - smoothstep(0.014, 0.05, Math.abs(radius - size))

        let value = outline * 0.85

        if (radius < size) {
            const inner = 0.5 + 0.5 * Math.cos(radius * 38 - t * 2)
            value = Math.max(value, inner * 0.32 * (1 - radius / size) + 0.04)
        }

        return value
    }

    return field
}

// Renders a stroked vector wordmark into a coverage mask at grid resolution, then shimmers it.
export const createWordmarkField = (
    paths: Array<string>,
    viewBox: [number, number],
    strokeWidth: number
): DotField => {
    let mask = new Float32Array(0)
    let maskCols = 0
    let maskRows = 0

    const field: DotField = (u, v, t, col, row) => {
        if (col >= maskCols || row >= maskRows) return 0

        const coverage = mask[row * maskCols + col]

        if (coverage <= 0.02) return 0

        const shimmer = 0.6 + 0.4 * Math.sin(u * 4.6 - t * 1.4 + Math.sin(v * 3 + t * 0.5) * 1.2)

        return coverage * shimmer
    }

    field.prepare = ({ cols, rows }) => {
        maskCols = cols
        maskRows = rows
        mask = new Float32Array(cols * rows)

        const canvas = document.createElement('canvas')
        canvas.width = cols
        canvas.height = rows

        const ctx = canvas.getContext('2d', { willReadFrequently: true })

        if (!ctx) return

        const [width, height] = viewBox
        const scale = Math.min((cols * 0.98) / width, (rows * 0.84) / height)

        ctx.setTransform(scale, 0, 0, scale, (cols - width * scale) / 2, (rows - height * scale) / 2)
        ctx.lineWidth = strokeWidth
        ctx.strokeStyle = '#000'
        paths.forEach(path => ctx.stroke(new Path2D(path)))

        const data = ctx.getImageData(0, 0, cols, rows).data

        for (let i = 0; i < mask.length; i++) mask[i] = data[i * 4 + 3] / 255
    }

    return field
}
